/** Design §9.2 enumerated date formats — the preview shows the parse of the sample. */

export const DATE_FORMATS = [
  'yyyy-MM-dd',
  'MM/dd/yyyy',
  'dd/MM/yyyy',
  'M/d/yyyy',
  'yyyyMMdd',
  'dd-MMM-yyyy',
  'MMM d, yyyy',
] as const;

export type DateFormat = (typeof DATE_FORMATS)[number];

const MONTHS: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

function civil(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) {
    return null;
  }
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

function monthIndex(token: string): number | null {
  const key = token.replace(/\./g, '').slice(0, 3).toLowerCase();
  return MONTHS[key] ?? null;
}

/** Parse `raw` with `format`; return `YYYY-MM-DD` or null. */
export function parseDateCell(raw: string, format: string): string | null {
  const value = raw.trim();
  if (value.length === 0) return null;
  switch (format) {
    case 'yyyy-MM-dd': {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      return m ? civil(Number(m[1]), Number(m[2]), Number(m[3])) : null;
    }
    case 'MM/dd/yyyy': {
      const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
      return m ? civil(Number(m[3]), Number(m[1]), Number(m[2])) : null;
    }
    case 'dd/MM/yyyy': {
      const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
      return m ? civil(Number(m[3]), Number(m[2]), Number(m[1])) : null;
    }
    case 'M/d/yyyy': {
      const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
      return m ? civil(Number(m[3]), Number(m[1]), Number(m[2])) : null;
    }
    case 'yyyyMMdd': {
      const m = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
      return m ? civil(Number(m[1]), Number(m[2]), Number(m[3])) : null;
    }
    case 'dd-MMM-yyyy': {
      const m = /^(\d{1,2})-([A-Za-z]{3,})-(\d{4})$/.exec(value);
      const token = m?.[2];
      if (m === null || token === undefined) return null;
      const month = monthIndex(token);
      return month === null ? null : civil(Number(m[3]), month, Number(m[1]));
    }
    case 'MMM d, yyyy': {
      const m = /^([A-Za-z]{3,})\s+(\d{1,2}),\s*(\d{4})$/.exec(value);
      const token = m?.[1];
      if (m === null || token === undefined) return null;
      const month = monthIndex(token);
      return month === null ? null : civil(Number(m[3]), month, Number(m[2]));
    }
    default:
      return null;
  }
}

/**
 * The date cell of the first data row the sample holds. `firstDataRow` is a 1-based file row and
 * `sampleRows[0]` is file row `preambleRows + 1` (TMMAPP-0123 item 3).
 */
export function firstSampleDateCell(
  sampleRows: string[][],
  preambleRows: number,
  firstDataRow: number,
  dateColumn: number | null,
): string {
  if (dateColumn === null) return '';
  const row = sampleRows[firstDataRow - 1 - preambleRows] ?? sampleRows[1] ?? sampleRows[0];
  if (row === undefined) return '';
  return row[dateColumn] ?? '';
}
